import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription as RxSubscription } from 'rxjs';
import { EventSource } from 'eventsource';
import * as dayjs from 'dayjs';
import { ApiUsage, Subscription, UsageService } from 'ldt-billing-service-api';
import { AuthService } from 'src/app/auth/service/auth.service';
import { BillingService } from 'src/app/core/services/billing.service';
import { Log } from 'src/app/logger.service';
import { environment } from 'src/environments/environment';

/**
 * Per-billing-cycle chat query usage for the Workforce.ai (chat) product.
 *
 * Limits (quota) and used counts both come from the billing usage endpoint + its
 * SSE stream, keyed by usage_type — so a plan change (e.g. a just-completed
 * upgrade) flows in live without a session refresh. Until the first usage payload
 * arrives we fall back to the org-settings limit (`org.settings.chat`). The plan
 * name comes from the org's subscriptions. A limit of `'unlimited'` (-1 on the
 * wire) means no cap; a missing limit falls back to the Free tier.
 */
export interface ChatCreditUsage {
  snapshotUsed: number;
  snapshotLimit: number | 'unlimited' | null;
  deepResearchUsed: number;
  deepResearchLimit: number | 'unlimited' | null;
  planName: string;
  // When the current usage cycle resets (next occurrence of the billing anchor day).
  resetDate: Date;
}

// Running totals for a single chat usage_type (the SSE stream sends one at a
// time). Each message carries the live quota (limit) for that type too, with -1
// meaning unlimited.
interface ChatUsageEntry {
  used: number;
  quota?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class ChatCreditsService {
  private static readonly CHAT_PRODUCT = 'chat';
  private static readonly SNAPSHOT_TYPE = 'snapshot';
  // The deep-research usage_type arrives in one of two spellings depending on the
  // backend path; the first is the canonical/default key.
  private static readonly DEEP_RESEARCH_TYPES = ['deep_research', 'deepResearch'];
  // Subscription statuses that still grant entitlement (i.e. count as the plan).
  private static readonly ACTIVE_STATUSES = ['active', 'trialing', 'past_due'];

  // Free-tier fallbacks (mirrors People's PEOPLE_QUOTA_FREE) used when the org
  // settings don't specify a limit for a dimension. `null` means unlimited; a
  // real number still wins. TODO: source these from the API.
  private static readonly SNAPSHOT_FREE_QUOTA = 15;
  private static readonly DEEP_RESEARCH_FREE_QUOTA = 3;

  // Usage warning thresholds, shared by every chat-usage display (sidebar gauge,
  // popover bars, composer caption) so they can never disagree: warn at 70% used
  // (or when <= 1 query is left, for small plans that never reach 70%), danger at
  // 90%.
  private static readonly USAGE_WARN_PERCENT = 70;
  private static readonly USAGE_DANGER_PERCENT = 90;
  private static readonly USAGE_LOW_REMAINING = 1;

  // Plan + limits from org settings (authoritative); used counts from usage/SSE.
  private snapshotLimit: number | 'unlimited' | null = ChatCreditsService.SNAPSHOT_FREE_QUOTA;
  private deepResearchLimit: number | 'unlimited' | null =
    ChatCreditsService.DEEP_RESEARCH_FREE_QUOTA;
  private planName = 'Free';
  // The org's chat subscription anchor day (used for paid-plan reset dates).
  private chatAnchorDate?: string | Date;
  // Next usage-cycle reset, derived from the plan + the chat subscription anchor.
  private resetDate: Date = dayjs().add(1, 'month').startOf('month').toDate();

  // Running used totals keyed by usage_type, fed by the initial fetch + SSE deltas.
  private usedByType = new Map<string, ChatUsageEntry>();
  private chatUsage$ = new BehaviorSubject<ChatCreditUsage | null>(null);
  private streamSub?: RxSubscription;

  /**
   * The chat backend expresses "no cap" as `null`; a number is a real cap. We
   * never receive -1 in practice, but the legacy -1 / 'unlimited' sentinels are
   * tolerated defensively so a single definition of "uncapped" lives here.
   */
  static isUnlimitedChatLimit(value: number | 'unlimited' | null | undefined): boolean {
    return value === null || value === 'unlimited' || value === -1;
  }

  /**
   * Resolve a raw chat limit (from org settings or the live usage quota) to the
   * consumer shape. `undefined` means "not specified" so the caller's fallback
   * wins; `null` (and the legacy sentinels) mean unlimited; otherwise the number.
   */
  static normalizeChatLimit(
    value: number | 'unlimited' | null | undefined,
    fallback: number | 'unlimited' | null
  ): number | 'unlimited' | null {
    if (value === undefined) {
      return fallback;
    }
    return ChatCreditsService.isUnlimitedChatLimit(value) ? 'unlimited' : (value as number);
  }

  /** Authoritative chat tier name for a Stripe price id. */
  static tierFromPriceId(priceId?: string | null): 'Pro' | 'Team' | 'Enterprise' {
    if (priceId === environment.chatProSubscriptionPriceId) {
      return 'Pro';
    }
    if (priceId === environment.chatTeamSubscriptionPriceId) {
      return 'Team';
    }
    return 'Enterprise';
  }

  /**
   * Severity of a single usage metric, shared by every usage display so the
   * gauge, the popover bars and the composer caption always agree. Callers map
   * the level to their own palette.
   */
  static usageSeverity(percent: number, remaining: number | null): 'ok' | 'warn' | 'danger' {
    if (percent >= ChatCreditsService.USAGE_DANGER_PERCENT) {
      return 'danger';
    }
    if (
      percent >= ChatCreditsService.USAGE_WARN_PERCENT ||
      (remaining !== null && remaining <= ChatCreditsService.USAGE_LOW_REMAINING)
    ) {
      return 'warn';
    }
    return 'ok';
  }

  constructor(
    private usageService: UsageService,
    private authService: AuthService,
    private billingService: BillingService
  ) {
    // Rebuild plan/limits (and the usage stream) whenever the active org changes.
    this.authService.$getSelectedOrg.subscribe((org) => this.initForOrg(org));

    // The current plan is determined from the org's subscriptions (available to
    // all org users), which is authoritative — used wherever we show the plan.
    this.billingService.subscriptions$.subscribe((subs) => {
      const plan = this.planFromSubscriptions(subs);
      if (plan && plan !== this.planName) {
        this.planName = plan;
        // The reset cadence depends on the plan (Free resets at month end), so
        // recompute it whenever the plan changes.
        this.resetDate = this.computeResetDate();
        if (this.chatUsage$.value) {
          this.emitUsage();
        }
      }
    });
  }

  /**
   * Live chat usage for the selected org. Plan + limits come from org settings;
   * used counts are seeded from `GET /{orgId}/usage?scope=org` and kept current
   * via the `/usage/stream` SSE feed, so making a chat updates the gauge live.
   */
  getChatCreditUsage(): Observable<ChatCreditUsage | null> {
    return this.chatUsage$.asObservable();
  }

  private initForOrg(org: any): void {
    this.teardownStream();
    this.usedByType.clear();

    if (!org) {
      this.chatUsage$.next(null);
      return;
    }

    // Limits straight from org settings (set by billing/Stripe). The plan name is
    // resolved separately from the org's subscriptions (see constructor). Reset it
    // to Free here so a previous org's plan never lingers before subscriptions for
    // the newly selected org load.
    this.planName = 'Free';
    const chat = org.settings?.chat ?? {};
    this.snapshotLimit = ChatCreditsService.normalizeChatLimit(
      chat.snapshotQueryLimit,
      ChatCreditsService.SNAPSHOT_FREE_QUOTA
    );
    this.deepResearchLimit = ChatCreditsService.normalizeChatLimit(
      chat.deepResearchQueryLimit,
      ChatCreditsService.DEEP_RESEARCH_FREE_QUOTA
    );
    this.chatAnchorDate = chat.subscriptionAnchorDate;
    this.resetDate = this.computeResetDate();

    // Emit plan/limits immediately (used = 0) so the UI is correct before usage loads.
    this.emitUsage();

    const orgId = org.id ?? this.authService.getSelectedOrgIdValue;
    if (!orgId) {
      return;
    }

    this.usageService.getOrgApiUsage(orgId, 'org').subscribe({
      next: (res) => {
        const chatItems = (res?.usage ?? []).filter(
          (item) => item.product_name === ChatCreditsService.CHAT_PRODUCT
        );
        for (const item of chatItems) {
          this.usedByType.set(item.usage_type, {
            used: item.total_usage,
            quota: item.quota,
          });
        }
        this.emitUsage();
        this.startUsageStream(orgId);
      },
      error: (err) => {
        Log.e('ChatCreditsService: failed to load initial chat usage', err);
        this.startUsageStream(orgId);
      },
    });
  }

  // SSE stream of usage updates; each message carries the new total for one
  // (product, usage_type). We merge chat deltas and re-emit. Mirrors the People
  // CreditUsageService so auth headers can be attached via the eventsource pkg.
  private startUsageStream(orgId: string): void {
    this.teardownStream();

    this.streamSub = new Observable<ApiUsage>((observer) => {
      const url = `${environment.billingApiBasePath}/${orgId}/usage/stream`;
      const eventSource = new EventSource(url, {
        fetch: (input: any, init: any) =>
          fetch(input, {
            ...init,
            headers: {
              ...init!.headers,
              Authorization: `Bearer ${this.authService.getAccessTokenValue}`,
            },
          }),
      });

      eventSource.onmessage = (event: any) => {
        try {
          observer.next(JSON.parse(event.data));
        } catch (error) {
          observer.error(error);
        }
      };

      eventSource.onerror = (error: any) => {
        observer.error(error);
        eventSource.close();
      };

      return () => eventSource.close();
    }).subscribe({
      next: (data) => {
        if (data.product_name !== ChatCreditsService.CHAT_PRODUCT) {
          return;
        }
        // Each message carries the live quota for this type, so a plan change
        // (e.g. just-completed upgrade) propagates the new limit here directly.
        this.usedByType.set(data.usage_type, { used: data.total_usage, quota: data.quota });
        this.emitUsage();
      },
      error: (err) => Log.e('ChatCreditsService: chat usage stream error', err),
    });
  }

  private teardownStream(): void {
    this.streamSub?.unsubscribe();
    this.streamSub = undefined;
  }

  private usedFor(type: string): number {
    return this.usedByType.get(type)?.used ?? 0;
  }

  // Resolve plan + limits + used counts into the consumer shape. Limits prefer the
  // live per-type quota from the usage/SSE payload (which reflects the current
  // plan the moment billing updates it, e.g. right after an upgrade), falling back
  // to the org-settings limit until the first usage payload arrives.
  private buildUsage(): ChatCreditUsage {
    const deepKey = this.deepResearchKey();
    return {
      snapshotUsed: this.usedFor(ChatCreditsService.SNAPSHOT_TYPE),
      snapshotLimit: this.limitFor(ChatCreditsService.SNAPSHOT_TYPE, this.snapshotLimit),
      deepResearchUsed: this.usedFor(deepKey),
      deepResearchLimit: this.limitFor(deepKey, this.deepResearchLimit),
      planName: this.planName,
      resetDate: this.resetDate,
    };
  }

  // Resolve whichever accepted deep-research spelling the backend sent for this
  // org, defaulting to the canonical key when no usage has arrived yet.
  private deepResearchKey(): string {
    return (
      ChatCreditsService.DEEP_RESEARCH_TYPES.find((type) => this.usedByType.has(type)) ??
      ChatCreditsService.DEEP_RESEARCH_TYPES[0]
    );
  }

  // Live quota for a usage_type (from usage/SSE) wins over the settings fallback.
  // We only fall back to the settings limit when no usage payload has arrived yet
  // for this type (no map entry at all). For a loaded type the live quota is
  // authoritative, and a null/missing quota means unlimited (so an uncapped
  // dimension stays off the gauge rather than inheriting the settings number).
  private limitFor(
    type: string,
    fallback: number | 'unlimited' | null
  ): number | 'unlimited' | null {
    const entry = this.usedByType.get(type);
    if (!entry) {
      return fallback; // usage not loaded yet for this type
    }
    return ChatCreditsService.isUnlimitedChatLimit(entry.quota ?? null)
      ? 'unlimited'
      : (entry.quota as number);
  }

  // Single emit point for chat usage.
  private emitUsage(): void {
    this.chatUsage$.next(this.buildUsage());
  }

  // Resolve the next usage-cycle reset. Free always resets at the calendar month
  // boundary (no per-subscription anchor); paid plans reset on their anchor day.
  private computeResetDate(): Date {
    if (this.planName === 'Free') {
      return dayjs().add(1, 'month').startOf('month').toDate();
    }
    return this.getNextResetDate(this.chatAnchorDate);
  }

  // Next occurrence of the anchor day-of-month (e.g. anchor=6 → next 6th); falls
  // back to the first of next month. Mirrors People's CreditUsageService.
  private getNextResetDate(anchorDate?: string | Date): Date {
    const fallback = dayjs().add(1, 'month').startOf('month').toDate();
    if (!anchorDate) {
      return fallback;
    }
    const anchor = dayjs(anchorDate);
    if (!anchor.isValid()) {
      return fallback;
    }
    const anchorDay = anchor.date();
    const today = dayjs();
    const targetMonth = today.date() >= anchorDay ? today.add(1, 'month') : today;
    const dayInTargetMonth = Math.min(anchorDay, targetMonth.daysInMonth());
    return targetMonth.date(dayInTargetMonth).startOf('day').toDate();
  }

  // Authoritative plan from the org's chat subscription: no *active* chat
  // subscription means Free; otherwise map the price id to the tier (a chat
  // subscription with a non-self-serve price is Enterprise). Canceled/expired
  // subscriptions are ignored so a downgraded org reads as Free. Returns null
  // until subscriptions load.
  private planFromSubscriptions(subs: Subscription[] | null): string | null {
    if (subs === null) {
      return null;
    }
    const chatSub = subs.find(
      (s) =>
        s.product?.id === environment.chatSubscriptionProductId &&
        ChatCreditsService.ACTIVE_STATUSES.includes((s.status ?? '').toLowerCase())
    );
    if (!chatSub) {
      return 'Free';
    }
    return ChatCreditsService.tierFromPriceId(chatSub.product?.price?.id);
  }
}
